import React, { useState } from "react";

import {
  Dialog,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import BoltIcon from "@mui/icons-material/Bolt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import useBetaLaunch from "app/hooks/globallyApplicable/useBetaLaunch";
import { muteBetaPromo } from "app/utils/beta";

/* Promo artwork lives in /public. The webp is ~142 KB against 1.5 MB for the
   png, so it loads first and the png is only the fallback. */
const PROMO_IMAGE_WEBP = "/new-erp-popup.webp";

/* The artwork already draws a "Try New ERP Now" pill. These are its measured
   bounds inside the 1536x1024 image, as percentages, so a real (invisible)
   button can sit exactly on top of it at any dialog width. */
const CTA_HOTSPOT = { left: "34.5%", width: "31%", top: "84.8%", height: "8.4%" };

/**
 * Text fallback used on phones (the landscape artwork is unreadable at that
 * width) and if the image itself fails to load. Answers the four doubts users
 * actually raise: "will I lose my data", "will my old ERP break", "do I need a
 * new login", "what do I even gain".
 */
const ASSURANCES = [
  {
    icon: <VerifiedUserIcon />,
    title: "Your data is 100% safe",
    desc: "Same account, same data. Nothing is copied, moved or deleted.",
  },
  {
    icon: <SwapHorizIcon />,
    title: "Nothing changes here",
    desc: "Your current ERP keeps working exactly as it is. Switch back any time.",
  },
  {
    icon: <LockOpenIcon />,
    title: "No new login needed",
    desc: "You're already signed in — we carry your session over in one click.",
  },
  {
    icon: <BoltIcon />,
    title: "Faster and easier to use",
    desc: "Instant loads, a cleaner design and a dashboard you can arrange yourself.",
  },
];

const BetaPromoPopup = ({ open, onClose, userDocId }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [imageFailed, setImageFailed] = useState(false);
  const { launchBeta } = useBetaLaunch();

  const showArtwork = !isMobile && !imageFailed;

  const handleLaunch = () => {
    launchBeta();
    onClose();
  };

  const handleNeverShowAgain = () => {
    muteBetaPromo(userDocId);
    onClose();
  };

  /* The cross is the only way out: MUI reports a reason ("backdropClick" /
     "escapeKeyDown") for every close it triggers itself, and we ignore those so
     a stray click outside the dialog never dismisses the promo. */
  const handleDialogClose = (event, reason) => {
    if (reason) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      maxWidth={showArtwork ? "lg" : "xs"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, sm: 4 },
          overflow: "hidden",
          m: { xs: 1.5, sm: 2 },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 5,
          color: "#334155",
          bgcolor: "rgba(255,255,255,0.85)",
          border: "1px solid rgba(15,23,42,0.08)",
          boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
          "&:hover": { bgcolor: "#fff" },
        }}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {showArtwork ? (
        <>
          {/* Shrink-wraps the image so the CTA hotspot's percentages stay
              locked to the artwork at any scale. */}
          <Box
            sx={{
              position: "relative",
              lineHeight: 0,
              width: "fit-content",
              maxWidth: "100%",
              mx: "auto",
            }}
          >
            <picture>
              <source srcSet={PROMO_IMAGE_WEBP} type="image/webp" />
              <Box
                component="img"
                src={PROMO_IMAGE_WEBP}
                alt="Ekalsutra has a new and improved beta version — same data, more possibilities"
                onError={() => setImageFailed(true)}
                sx={{
                  display: "block",
                  width: "auto",
                  maxWidth: "100%",
                  maxHeight: "calc(100vh - 220px)",
                }}
              />
            </picture>

            {/* Real button sitting on the CTA drawn into the artwork. */}
            <Box
              component="button"
              type="button"
              onClick={handleLaunch}
              aria-label="Try the new ERP beta now"
              sx={{
                position: "absolute",
                ...CTA_HOTSPOT,
                p: 0,
                border: "none",
                background: "transparent",
                borderRadius: "999px",
                cursor: "pointer",
                transition: "box-shadow 0.2s ease",
                "&:hover": {
                  boxShadow: "0 0 0 3px rgba(255,255,255,0.9)",
                },
                "&:focus-visible": {
                  outline: "3px solid #fff",
                  outlineOffset: 2,
                },
              }}
            />
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            sx={{
              px: 3,
              py: 1.75,
              borderTop: "1px solid #eef2f8",
              bgcolor: "#fbfcfe",
            }}
          >
            <Button
              onClick={handleNeverShowAgain}
              disableRipple
              sx={{
                textTransform: "none",
                p: 0,
                minWidth: 0,
                fontSize: 12.5,
                fontWeight: 500,
                color: "#94A3B8",
                "&:hover": { background: "transparent", color: "#5A7184" },
              }}
            >
              Don&apos;t show this again
            </Button>

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#94A3B8",
                  display: { xs: "none", md: "block" },
                }}
              >
                Opens in a new tab · Your current ERP stays exactly as it is
              </Typography>
              <Button
                onClick={onClose}
                sx={{
                  textTransform: "none",
                  color: "#5A7184",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  "&:hover": { background: "rgba(90,113,132,0.08)" },
                }}
              >
                Remind me tomorrow
              </Button>
              <Button
                onClick={handleLaunch}
                endIcon={<ArrowForwardIcon />}
                disableElevation
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14.5,
                  color: "#fff",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  whiteSpace: "nowrap",
                  background: "linear-gradient(110deg, #3c6eef, #7a5cff)",
                  boxShadow: "0 6px 16px rgba(60, 110, 239, 0.35)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    background: "linear-gradient(110deg, #345fd6, #6a4bd8)",
                    boxShadow: "0 8px 20px rgba(122, 92, 255, 0.45)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Try New ERP Now
              </Button>
            </Stack>
          </Stack>
        </>
      ) : (
        <Box sx={{ px: 2.5, py: 3 }}>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              fontSize: 12,
              fontWeight: 600,
              color: "#3c6eef",
              px: 1.25,
              py: 0.5,
              borderRadius: "999px",
              bgcolor: "#eef3ff",
              border: "1px solid #dbe5ff",
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 14 }} />
            New &amp; Improved Beta Version
          </Box>

          <Typography
            sx={{
              mt: 1.5,
              fontWeight: 700,
              fontSize: 21,
              lineHeight: 1.25,
              color: "#0c2f49",
            }}
          >
            A Better Ekalsutra for a Brighter Tomorrow
          </Typography>
          <Typography
            sx={{ mt: 0.75, fontSize: 13.5, color: "#5A7184", lineHeight: 1.5 }}
          >
            Same data. More possibilities. A modern look, a smoother experience
            and more powerful features — your old ERP stays right here.
          </Typography>

          <Stack spacing={1} sx={{ mt: 2 }}>
            {ASSURANCES.map((item) => (
              <Stack
                key={item.title}
                direction="row"
                alignItems="flex-start"
                spacing={1.25}
              >
                <Box
                  sx={{
                    flex: "none",
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#3c6eef",
                    background: "linear-gradient(135deg, #e8f0fe, #eae6ff)",
                    "& svg": { fontSize: 19 },
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#0c2f49",
                      lineHeight: 1.35,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12.5, color: "#5a7184", lineHeight: 1.4 }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          <Stack spacing={1} sx={{ mt: 2.5 }}>
            <Button
              fullWidth
              onClick={handleLaunch}
              endIcon={<ArrowForwardIcon />}
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 15,
                color: "#fff",
                borderRadius: "12px",
                py: 1.25,
                background: "linear-gradient(110deg, #3c6eef, #7a5cff)",
                boxShadow: "0 6px 16px rgba(60, 110, 239, 0.35)",
                "&:hover": {
                  background: "linear-gradient(110deg, #345fd6, #6a4bd8)",
                },
              }}
            >
              Try New ERP Now
            </Button>
            <Button
              fullWidth
              onClick={onClose}
              sx={{
                textTransform: "none",
                color: "#5A7184",
                fontWeight: 600,
                borderRadius: 2,
                py: 1,
                "&:hover": { background: "rgba(90,113,132,0.08)" },
              }}
            >
              Remind me tomorrow
            </Button>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1.25 }}
          >
            <Typography sx={{ fontSize: 11.5, color: "#94A3B8" }}>
              Opens in a new tab
            </Typography>
            <Button
              onClick={handleNeverShowAgain}
              disableRipple
              sx={{
                textTransform: "none",
                p: 0,
                minWidth: 0,
                fontSize: 11.5,
                fontWeight: 500,
                color: "#94A3B8",
                "&:hover": { background: "transparent", color: "#5A7184" },
              }}
            >
              Don&apos;t show this again
            </Button>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
};

export default BetaPromoPopup;
